// ===========================================
// Peak Hour Heatmap Component
// ===========================================
// Heatmap showing lesson density by hour with peak hour highlighting

import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { Whatshot as PeakIcon } from '@mui/icons-material';

import { PeakHourData } from '../../api/reports.api';

// ===========================================
// TYPES
// ===========================================

interface PeakHourHeatmapProps {
  data: PeakHourData[];
  loading?: boolean;
}

// ===========================================
// HELPERS
// ===========================================

// Format hour for display (24h to 12h format)
const formatHour = (hour: number): string => {
  if (hour === 0) return '12 AM';
  if (hour === 12) return '12 PM';
  if (hour < 12) return `${hour} AM`;
  return `${hour - 12} PM`;
};

// Get color intensity based on lesson count
const getHeatColor = (count: number, maxCount: number): string => {
  if (maxCount === 0) return '#FCF6E6'; // Cream background
  const intensity = count / maxCount;

  if (intensity >= 0.8) return '#4580E4'; // Primary blue - very busy
  if (intensity >= 0.6) return '#a3d9f6'; // Light blue - busy
  if (intensity >= 0.4) return '#96DAC9'; // Mint - moderate
  if (intensity >= 0.2) return '#FFAE9E'; // Coral - light
  return '#FCF6E6'; // Cream - minimal
};

// ===========================================
// COMPONENT
// ===========================================

export default function PeakHourHeatmap({ data, loading }: PeakHourHeatmapProps) {
  // Loading state
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Peak Hours Analysis
          </Typography>
          <Alert severity="info">
            No peak hour data available for the selected period.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Calculate max lesson count for color scaling
  const maxLessonCount = Math.max(...data.map(d => d.lessonCount));
  const peakHours = data.filter(d => d.isPeak);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Peak Hours Analysis
          </Typography>
          {peakHours.length > 0 && (
            <Chip
              icon={<PeakIcon />}
              label={`${peakHours.length} Peak Hours`}
              color="warning"
              size="small"
            />
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
          Lesson density throughout the day
        </Typography>

        {/* Heatmap Grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mb: 2 }}>
          {data.map((hourData) => (
            <Box
              key={hourData.hour}
              sx={{
                position: 'relative',
                p: 1.5,
                borderRadius: 1,
                bgcolor: getHeatColor(hourData.lessonCount, maxLessonCount),
                border: hourData.isPeak ? 2 : 1,
                borderColor: hourData.isPeak ? 'warning.main' : 'divider',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: 2,
                },
              }}
            >
              {hourData.isPeak && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                  }}
                >
                  <PeakIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                </Box>
              )}
              <Typography
                variant="caption"
                fontWeight={600}
                display="block"
                sx={{ color: hourData.lessonCount > maxLessonCount * 0.6 ? 'white' : 'text.primary' }}
              >
                {formatHour(hourData.hour)}
              </Typography>
              <Typography
                variant="h6"
                sx={{ color: hourData.lessonCount > maxLessonCount * 0.6 ? 'white' : 'text.primary' }}
              >
                {hourData.lessonCount}
              </Typography>
              <Typography
                variant="caption"
                display="block"
                sx={{ color: hourData.lessonCount > maxLessonCount * 0.6 ? 'white' : 'text.secondary' }}
              >
                {hourData.utilizationRate.toFixed(0)}%
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Legend */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            Intensity:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#FCF6E6', border: 1, borderColor: 'divider', borderRadius: 0.5 }} />
            <Typography variant="caption">Low</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#96DAC9', borderRadius: 0.5 }} />
            <Typography variant="caption">Medium</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#4580E4', borderRadius: 0.5 }} />
            <Typography variant="caption">High</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
